import { useCallback, useState, useMemo, useEffect } from "react"

import { useMatch } from "react-router-dom"

import { useGeoServerFeaturesList } from "@l2r-front/l2r-geodata"
import { useMapContext } from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"
import { BottomSheetHeights, NavigationOrigins, useApplicationDispatchContext, useApplicationStateContext, useCrossTabState } from "@l2r-front/l2r-utils"

import { IMAGE_COLLECTION_TYPE_PHOTO, IMAGE_COLLECTION_TYPE_STREETVIEW, IMAGE_COLLECTION_TYPES } from "../../constants/imageCollectionsTypes"
import { imageCollectionQueryKeys } from "../../hooks/queries/imageCollection/queryKeys"
import { useLatestImageCollection } from "../../hooks/queries/imageCollection/useLatestImageCollection"

import { ImageCollectionDispatchContext, ImageCollectionStateContext, initialState } from "./ImageCollectionContext.context"

export const ImageCollectionContextProvider = (props) => {
    const { children } = props
    const { selectedNetwork } = useNetworksStateContext()
    const isMobile = useIsMobileDevice()
    const matchDetailView = useMatch(":slug/:appId/:moduleId/:road/:detailId/*")
    const matchRoadView = useMatch(":slug/:appId/:moduleId/:road")

    const { data: currentRoad } = useGeoServerFeaturesList(imageCollectionQueryKeys.list(decodeURIComponent(matchRoadView?.params.road)), {
        layer: selectedNetwork.referential.layerSection,
        linear_location_road: decodeURIComponent(matchRoadView?.params.road),
    }, {
        enabled: !!matchRoadView?.params.road && !matchDetailView?.params.detailId,
    })

    const [mapState, mapDispatch] = useMapContext()
    const { setImageLayerDisplayed, setDesktopOpenPhoto, setMapHeightPct, setSelectedImageCollectionType } = mapDispatch
    const { desktopOpenPhoto, imageLayerDisplayed, selectedImageCollectionType } = mapState
    const { setCurrentBottomSheetHeight } = useApplicationDispatchContext()
    const { navigationOrigin } = useApplicationStateContext()

    const [selectedImageCollectionRoad, setSelectedImageCollectionRoad] = useCrossTabState("selectedImageCollectionRoad", null)
    const [selectedImageCurrentYear, setImageCollectionCurrentYear] = useCrossTabState("selectedImageCurrentYear", null)
    const [triggeringFeatureId, setTriggeringFeatureId] = useCrossTabState("triggeringFeatureId", null)
    const [displayedImagePosition, setDisplayedImagePosition] = useCrossTabState("displayedImagePosition", null)
    const [imageCollectionState, setImageCollectionState] = useState(initialState)
    const [imageFeatureClickedPosition, setImageFeatureClickedPosition] = useCrossTabState("imageFeatureClickedPosition", null)
    const [imageAngle, setImageAngle] = useCrossTabState("imageAngleClicked", null)

    const {
        data: imageCollection,
        isLoading: isLoadingImageCollections,
    } = useLatestImageCollection(selectedNetwork?.slug, selectedImageCurrentYear)

    const setImageIdClicked = useCallback((imageId) => {
        setImageCollectionState(value => ({
            ...value,
            imageIdClicked: imageId,
            segmentIdClicked: null,
        }))
    }, [setImageCollectionState])

    const setSegmentIdClicked = useCallback((segmentId) => {
        setImageCollectionState(value => ({
            ...value,
            segmentIdClicked: segmentId,
            imageIdClicked: null,
        }))
    }, [setImageCollectionState])

    const setSegmentFeatureIdClicked = useCallback((segmentFeatureId) => {
        setSegmentIdClicked(segmentFeatureId)
    }, [setSegmentIdClicked])

    const isImageCollectionAvailable = useCallback(() => {
        return !!imageCollection && (IMAGE_COLLECTION_TYPES.includes(imageCollection.type) || !isMobile)
    }, [imageCollection, isMobile])

    useEffect(() => {
        if (!isLoadingImageCollections && selectedImageCollectionType === IMAGE_COLLECTION_TYPE_PHOTO) {
            setSelectedImageCollectionType(imageCollection ? imageCollection.type : IMAGE_COLLECTION_TYPE_STREETVIEW)
        }
    }, [imageCollection, isLoadingImageCollections, selectedImageCollectionType, setSelectedImageCollectionType])

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            if (imageCollectionState.imageIdClicked) {
                setTriggeringFeatureId(imageCollectionState.imageIdClicked)
            } else if (imageCollectionState.segmentIdClicked) {
                setTriggeringFeatureId(imageCollectionState.segmentIdClicked)
            }
            return cancelAnimationFrame(frame)
        })
    }, [imageCollectionState.imageIdClicked, imageCollectionState.segmentIdClicked, setTriggeringFeatureId])

    const resetImageCollectionType = useCallback(() => {
        const imageCollectionType = imageCollection?.type ? imageCollection?.type : IMAGE_COLLECTION_TYPE_STREETVIEW
        setSelectedImageCollectionType(imageCollectionType)
    }, [setSelectedImageCollectionType, imageCollection?.type])

    const resetImageCollectionDisplay = useCallback(() => {
        setImageLayerDisplayed(false)
        resetImageCollectionType()
        setDisplayedImagePosition(null)
    }, [setImageLayerDisplayed, setDisplayedImagePosition, resetImageCollectionType])

    const openImageViewer = useCallback(() => {
        setMapHeightPct(50)
        setCurrentBottomSheetHeight(BottomSheetHeights.CLOSED)
    }, [setCurrentBottomSheetHeight, setMapHeightPct])

    useEffect(() => {
        const handleChildWindowClosed = (event) => {
            if (event.data === "childWindowClosed") {
                resetImageCollectionDisplay()
            }
        }

        window.addEventListener("message", handleChildWindowClosed)

        return () => {
            window.removeEventListener("message", handleChildWindowClosed)
        }
    }, [resetImageCollectionDisplay])

    const onImageViewerButtonClicked = useCallback(() => {
        openImageViewer()
        setImageLayerDisplayed(true)
    }, [openImageViewer, setImageLayerDisplayed])

    const onSwitchImageLayer = useCallback((event) => {
        setImageLayerDisplayed(event.target.checked)
    }, [setImageLayerDisplayed])

    const setImageFeatureIdClicked = useCallback((imageFeatureId) => {
        setImageFeatureClickedPosition(null)
        setImageIdClicked(imageFeatureId)
        if (imageFeatureId) {
            openImageViewer()
        }
    }, [setImageFeatureClickedPosition, setImageIdClicked, openImageViewer])

    const setImageAngleClicked = useCallback((imageAngle) => {
        setImageAngle(imageAngle)
    }, [setImageAngle])

    useEffect(() => {
        const road = decodeURIComponent(matchRoadView?.params.road || matchDetailView?.params.road)
        if (road && road !== "undefined") {
            setSelectedImageCollectionRoad(road)
        }
        if (!road || road === "undefined") {
            resetImageCollectionDisplay()
        }
    }, [
        matchRoadView?.params.road,
        matchDetailView,
        setSelectedImageCollectionRoad,
        resetImageCollectionDisplay])

    useEffect(() => {
        if (desktopOpenPhoto) {
            setImageLayerDisplayed(true)
            openImageViewer()
            setDesktopOpenPhoto(false)
        }
    }, [desktopOpenPhoto, imageCollection, openImageViewer, setDesktopOpenPhoto, setImageLayerDisplayed, setSelectedImageCollectionType])

    useEffect(() => {
        if (matchDetailView?.params?.detailId || navigationOrigin === NavigationOrigins.ROAD_FROM_BACK_DETAIL) {
            return
        }

        if (matchRoadView && !imageCollectionState.imageIdClicked) {
            setImageFeatureClickedPosition(null)

            const currentRoadId = currentRoad?.[0]?.uuid
            if (!!currentRoadId && triggeringFeatureId !== currentRoadId) {
                setTriggeringFeatureId(currentRoadId)
                setSegmentFeatureIdClicked(currentRoadId)
            }
        }
    }, [currentRoad,
        navigationOrigin,
        matchDetailView,
        matchRoadView,
        setSegmentFeatureIdClicked,
        setImageFeatureClickedPosition,
        setTriggeringFeatureId,
        triggeringFeatureId,
        imageCollectionState.imageIdClicked])

    // When coming back from a detail, keep the clicked position in sync with the displayed image
    useEffect(() => {
        if (matchDetailView?.params?.detailId || !matchRoadView || navigationOrigin !== NavigationOrigins.ROAD_FROM_BACK_DETAIL) {
            return
        }
        if (displayedImagePosition && !imageCollectionState.imageIdClicked) {
            setImageFeatureClickedPosition([displayedImagePosition.lng, displayedImagePosition.lat])
        }
    }, [displayedImagePosition, navigationOrigin, matchDetailView, matchRoadView, imageCollectionState.imageIdClicked, setImageFeatureClickedPosition])

    const stateValue = useMemo(() => {
        return {
            displayedImagePosition: displayedImagePosition,
            triggeringFeatureId: triggeringFeatureId,
            selectedImageCollectionType: selectedImageCollectionType,
            selectedImageCollectionRoad: selectedImageCollectionRoad,
            imageAngle,
            imageLayerDisplayed,
            imageCollection: imageCollection,
            imageFeatureClickedPosition,
            isLoadingImageCollections,
        }
    }, [
        displayedImagePosition,
        imageLayerDisplayed,
        triggeringFeatureId,
        selectedImageCollectionType,
        selectedImageCollectionRoad,
        imageAngle,
        imageCollection,
        imageFeatureClickedPosition,
        isLoadingImageCollections,
    ])

    const dispatchValue = useMemo(() => {
        return {
            onOpenImageViewer: matchRoadView?.params.road ? onImageViewerButtonClicked : null,
            onSwitchImageLayer: matchRoadView?.params.road ? onSwitchImageLayer : null,
            setImageLayerDisplayed,
            setDisplayedImagePosition,
            resetImageCollectionType,
            resetImageCollectionDisplay,
            setTriggeringFeatureId,
            setSelectedImageCollectionType,
            setImageAngle: setImageAngleClicked,
            setImageFeatureIdClicked,
            setImageFeatureClickedPosition,
            setSegmentFeatureIdClicked,
            isImageCollectionAvailable,
            setImageCollectionCurrentYear,
        }
    }, [
        matchRoadView?.params.road,
        onImageViewerButtonClicked,
        onSwitchImageLayer,
        setImageLayerDisplayed,
        setDisplayedImagePosition,
        resetImageCollectionType,
        resetImageCollectionDisplay,
        setSelectedImageCollectionType,
        setTriggeringFeatureId,
        setImageAngleClicked,
        setImageFeatureClickedPosition,
        setImageFeatureIdClicked,
        setSegmentFeatureIdClicked,
        isImageCollectionAvailable,
        setImageCollectionCurrentYear,
    ])

    return (
        <ImageCollectionStateContext.Provider value={stateValue}>
            <ImageCollectionDispatchContext.Provider value={dispatchValue}>
                {children}
            </ImageCollectionDispatchContext.Provider>
        </ImageCollectionStateContext.Provider>
    )
}

ImageCollectionContextProvider.propTypes = {
    children: PropTypes.node,
}